import api from 'api'
import { ImpressaoAcompanhamentoMChatInput } from 'api/ImpressaoMChat'
import { Button, HFlow, Icon, Text, Tooltip } from 'bold-ui'
import { usePecField } from 'components/form'
import { confirm } from 'components/modals/confirm'
import { NavigationButton } from 'components/route/NavigationButton'
import { useRegistrosMChatQuery } from 'graphql/hooks.generated'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useServerTime } from 'hooks/useServerTime'
import { isEmpty } from 'util/checks'
import { MetaPath } from 'util/metaPath'

import { MChatRfAccordionSection, MChatRfAccordionSectionProps } from '../../components/MChatRfAccordionSection'
import { MChatRfAccordionStepper } from '../../components/MChatRfAccordionStepper'
import { MChatRfAccordionSectionEnum } from '../../model-mChatRf'
import { convertMChatFieldToViewModel, convertMChatToPerguntaRespostaInput } from '../converter-mchat'
import { MChatFieldModel, MChatViewModel } from '../model-mchat'
import { getAlertaMChatAnterior } from '../utils-mchat'

interface MChatFieldAccordionSectionProps
  extends Omit<
    MChatRfAccordionSectionProps,
    'section' | 'value' | 'stepper' | 'primaryActions' | 'secondaryActions' | 'footerInfo'
  > {
  name: MetaPath<MChatFieldModel>
  valuesAtendimentoAnterior: MChatViewModel | null
  hasConsultaSeguimento: boolean
  modalUrl: string
  onDelete: () => void
}

export function MChatFieldAccordionSection(props: Readonly<MChatFieldAccordionSectionProps>) {
  const { name, valuesAtendimentoAnterior, modalUrl, hasConsultaSeguimento, onDelete, ...sectionProps } = props

  const { getServerTimeNow } = useServerTime()

  const {
    atendimentoProfissional: { id: atendProfId } = {},
    cidadao: { id: cidadaoId },
    prontuario: { id: prontuarioId },
  } = useAtendimentoContext()

  const {
    input: { value },
  } = usePecField({ name, subscription: { value: true } })

  const { data: { registrosMChat } = {} } = useRegistrosMChatQuery({ variables: { prontuarioId } })

  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao<ImpressaoAcompanhamentoMChatInput>()

  const hasDadosAtendimentoAtual = !isEmpty(value)
  const hasDadosAtendimentoAnterior = !isEmpty(valuesAtendimentoAnterior)

  const isRiscoMedio = hasDadosAtendimentoAtual
    ? value?.risco === MChatRiscoEnum.MEDIO
    : valuesAtendimentoAnterior?.risco === MChatRiscoEnum.MEDIO
  const isConsultaSeguimentoAtiva = (hasDadosAtendimentoAtual || hasDadosAtendimentoAnterior) && isRiscoMedio
  const isReavaliar = hasDadosAtendimentoAnterior && !hasDadosAtendimentoAtual
  const mChatToPrint = hasDadosAtendimentoAtual ? value : valuesAtendimentoAnterior
  const alertaMChatAnterior = getAlertaMChatAnterior(value?.risco, getServerTimeNow(), registrosMChat)

  const handlePrint = () => {
    const input = {
      atendProfId: hasDadosAtendimentoAtual
        ? atendProfId
        : valuesAtendimentoAnterior?.footerInfo?.atendimentoProfissionalId,
      cidadaoId: cidadaoId,
      resultado: mChatToPrint?.resultado,
      risco: mChatToPrint?.risco,
      aplicacao: convertMChatToPerguntaRespostaInput(mChatToPrint),
    }

    handleImpressaoPath({
      apiPath: api.mchat.imprimirMChat,
      values: input,
      file: `MChat.pdf`,
      dateFormat: reportFormatDate,
    }).catch(handleImpressaoError)
  }

  const handleDelete = () => confirmDeleteDialog(onDelete)

  const resolvedValue = hasDadosAtendimentoAtual ? convertMChatFieldToViewModel(value) : valuesAtendimentoAnterior

  return (
    <MChatRfAccordionSection
      section={MChatRfAccordionSectionEnum.MCHAT}
      value={resolvedValue}
      isConsultaSeguimentoAtiva={isConsultaSeguimentoAtiva}
      hideDivider
      stepper={
        <MChatRfAccordionStepper
          showPendingIcon={!hasDadosAtendimentoAtual && !hasDadosAtendimentoAnterior}
          showCheckIcon={hasDadosAtendimentoAtual || hasDadosAtendimentoAnterior}
          showConnector={isRiscoMedio && !hasConsultaSeguimento}
          showInfoIcon={false}
          showErrorIcon={false}
        />
      }
      primaryActions={
        !hasDadosAtendimentoAtual && (
          <NavigationButton kind={!isReavaliar ? 'primary' : 'normal'} size='small' to={modalUrl}>
            {isReavaliar ? 'Reavaliar' : 'Avaliar'}
          </NavigationButton>
        )
      }
      secondaryActions={
        <HFlow hSpacing={0.5}>
          {(hasDadosAtendimentoAnterior || hasDadosAtendimentoAtual) && (
            <Tooltip text='Imprimir'>
              <Button type='button' kind='normal' skin='ghost' size='small' onClick={handlePrint}>
                <Icon icon='printerOutline' />
              </Button>
            </Tooltip>
          )}
          {hasDadosAtendimentoAtual && (
            <HFlow hSpacing={0.5}>
              <Tooltip text='Editar'>
                <NavigationButton
                  type='button'
                  kind='normal'
                  skin='ghost'
                  size='small'
                  disabled={!hasDadosAtendimentoAtual}
                  to={`${modalUrl}/editar`}
                >
                  <Icon icon='penOutline' />
                </NavigationButton>
              </Tooltip>
              <Tooltip text='Excluir'>
                <Button type='button' kind='normal' skin='ghost' size='small' onClick={handleDelete}>
                  <Icon icon='trashOutline' />
                </Button>
              </Tooltip>
            </HFlow>
          )}
        </HFlow>
      }
      isRegistradoAgora={hasDadosAtendimentoAtual}
      alertaMChatAnterior={alertaMChatAnterior}
      {...sectionProps}
    />
  )
}

const confirmDeleteDialog = (handleConfirm: () => void) => {
  confirm({
    title: 'Deseja excluir a avaliação?',
    icon: 'exclamationTriangleOutline',
    confirmLabel: 'Excluir',
    cancelLabel: 'Cancelar',
    type: 'danger',
    size: 'auto',
    onConfirm: handleConfirm,
    render: () => <Text>As informações registradas e o resultado do M-CHAT-R™ serão perdidos.</Text>,
  })()
}
